import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverStreetFoodSecrets() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-street-food-secrets";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles?.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Japadog – Japanese-Style Hot Dogs",
    "section-2": "Tacofino – Baja Tacos on Wheels",
    "section-3": "Mom’s Grilled Cheese Truck",
    "section-4": "Chickpea – Vegan Middle Eastern Flavors",
    "section-5": "The Frying Pan – Korean Fried Chicken",
    "section-6": "Arturo’s Mexico To Go",
    "section-7": "Il Saltimbocca – Wood-Fired Italian Sandwiches",
    "section-8": "Reel Mac and Cheese – Cheesy Comfort",
    "section-9": "Shameless Buns – Filipino Fusion",
    "section-10": "Super Thai Food – Authentic Thai Street Eats",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://streetfoodapp.com/vancouver",
      label: "Vancouver Street Food App",
    },
    {
      url: "https://vancouver.ca/doing-business/roaming-food-vending.aspx",
      label: "City of Vancouver – Food Vending Regulations",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.28220,
      lng: -123.12430,
      popupText: "Japadog – Burrard & Smithe",
    },
    {
      id: "section-2",
      lat: 49.28400,
      lng: -123.11700,
      popupText: "Tacofino – Financial District",
    },
    {
      id: "section-3",
      lat: 49.28315,
      lng: -123.12030,
      popupText: "Mom’s Grilled Cheese Truck – Howe & Georgia",
    },
    {
      id: "section-4",
      lat: 49.26766,
      lng: -123.15500,
      popupText: "Chickpea Food Truck – 4th & Yew",
    },
    {
      id: "section-5",
      lat: 49.28660,
      lng: -123.11849,
      popupText: "The Frying Pan – 505 Burrard St",
    },
    {
      id: "section-6",
      lat: 49.28790,
      lng: -123.11460,
      popupText: "Arturo’s Mexico To Go – Howe & Cordova",
    },
    {
      id: "section-7",
      lat: 49.28730,
      lng: -123.11950,
      popupText: "Il Saltimbocca – Burrard & Pender",
    },
    {
      id: "section-8",
      lat: 49.28273,
      lng: -123.03730,
      popupText: "Reel Mac and Cheese – PNE Grounds",
    },
    {
      id: "section-9",
      lat: 49.23400,
      lng: -123.09000,
      popupText: "Shameless Buns – Fraser St (Approx.)",
    },
    {
      id: "section-10",
      lat: 49.26865,
      lng: -123.09830,
      popupText: "Super Thai Food – Great Northern Way",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Street Food Secrets: 10 Must-Try Spots That Will Blow Your Mind</title>
        <meta
          name="description"
          content="Discover Vancouver's street food scene with these 10 top outdoor eats. Tips, addresses, operating hours, local regulations, FAQ, and more!"
        />
        <meta
          name="keywords"
          content="Vancouver street food, food trucks, outdoor eats, Japadog, Tacofino, local regulations"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-street-food-secrets.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Street Food Secrets: 10 Must-Try Spots That Will Blow Your Mind"
        />
        <meta
          property="og:description"
          content="Your go-to guide for Vancouver’s top street food spots, complete with addresses, costs, and local tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-street-food-secrets.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-street-food-secrets.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Street Food Secrets: 10 Must-Try Spots That Will Blow Your Mind"
        />
        <meta
          name="twitter:description"
          content="Your go-to guide for Vancouver’s top street food spots, complete with addresses, costs, and local tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-street-food-secrets.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={ref_data?.["image-desc"] || "Vancouver Street Food"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s street food scene is a vibrant blend of local innovation
              and multicultural flavors. From fresh fish tacos to mouthwatering
              fusion hot dogs, these mobile kitchens have gained a dedicated
              following for good reason. In this article, we’ll unveil ten
              must-try street food spots that will blow your mind, complete with
              practical tips, coordinates, local regulations, and FAQ to help you
              make the most of Vancouver’s top outdoor eats.
            </p>

            {/* SECTION-1: Japadog */}
            <h2 id="section-1">Japadog – Japanese-Style Hot Dogs</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Burrard & Smithe (Coordinates: 49.28220, -123.12430){" "}
              <Marker
                coordY={49.28220}
                coordX={-123.12430}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <br />
              <strong>Hours:</strong> 11:00 AM – 10:00 PM daily <br />
              <strong>Cost:</strong> Hot dogs ~$5–$9
            </p>
            <p>
              Famous for its bold Japanese fusion hot dogs, Japadog put Vancouver’s
              street food on the global map. The signature Terimayo – topped with
              teriyaki sauce, Japanese mayo, and nori seaweed – is a savory sensation.
              Expect lines at peak lunch and dinner hours.
            </p>

            {/* SECTION-2: Tacofino */}
            <h2 id="section-2">Tacofino – Baja Tacos on Wheels</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Financial District (Coordinates: 49.28400, -123.11700){" "}
              <Marker
                coordY={49.28400}
                coordX={-123.11700}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Typically 11:30 AM – 2:30 PM (weekdays) <br />
              <strong>Cost:</strong> ~$6–$8 per taco
            </p>
            <p>
              Originally hailing from Tofino, this beloved truck serves up delicious
              Baja-inspired eats like the crispy fish taco and hearty burritos.
              Their location can change, so check daily schedules on apps or social
              media. A perfect pick for fresh, sustainably sourced seafood with a
              west coast twist.
            </p>

            {/* SECTION-3: Mom’s Grilled Cheese Truck */}
            <h2 id="section-3">Mom’s Grilled Cheese Truck</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Howe & Georgia (Coordinates: 49.28315, -123.12030){" "}
              <Marker
                coordY={49.28315}
                coordX={-123.12030}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Weekdays ~11:30 AM – 3:00 PM <br />
              <strong>Cost:</strong> ~$8–$12
            </p>
            <p>
              Craving classic comfort food? Mom’s Grilled Cheese Truck offers
              ooey-gooey sandwiches bursting with melted cheddar, veggies, or even
              mac & cheese on sourdough. Pair with a cup of tomato soup, and you’ve
              got the ultimate nostalgic lunch. The lines can be long, but the cheesy
              bliss is well worth the wait.
            </p>

            {/* SECTION-4: Chickpea */}
            <h2 id="section-4">Chickpea – Vegan Middle Eastern Flavors</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> 4th & Yew (Kitsilano) (Coordinates: 49.26766, -123.15500){" "}
              <Marker
                coordY={49.26766}
                coordX={-123.15500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Thurs–Sun, ~11:00 AM – 9:00 PM <br />
              <strong>Cost:</strong> $10–$15
            </p>
            <p>
              If you’re seeking a hearty, plant-based meal, Chickpea has you covered.
              Their falafel pita, crispy chickpea fries, and flavorful Shakshuka
              wraps are local favorites. Portions are generous, and the friendly vibe
              makes it a must-visit for vegans and omnivores alike.
            </p>

            {/* SECTION-5: The Frying Pan */}
            <h2 id="section-5">The Frying Pan – Korean Fried Chicken</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> 505 Burrard St (Coordinates: 49.28660, -123.11849){" "}
              <Marker
                coordY={49.28660}
                coordX={-123.11849}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Weekday lunches ~11:00 AM – 2:30 PM <br />
              <strong>Cost:</strong> ~$12–$15 for a chicken sandwich
            </p>
            <p>
              Specializing in crispy Korean-style fried chicken sandwiches, The
              Frying Pan stands out with spicy sauces and crunchy slaw. Pair your
              sandwich with kimchi fries for a fun, flavor-packed meal. Go early – they
              often sell out before closing.
            </p>

            {/* SECTION-6: Arturo’s Mexico To Go */}
            <h2 id="section-6">Arturo’s Mexico To Go</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Howe & Cordova (Coordinates: 49.28790, -123.11460){" "}
              <Marker
                coordY={49.28790}
                coordX={-123.11460}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> ~11:00 AM – 3:00 PM (weekdays) <br />
              <strong>Cost:</strong> $4–$7 per taco, $10–$12 burritos
            </p>
            <p>
              Bringing authentic Mexican street eats to downtown Vancouver, Arturo’s
              is known for delicious burritos, tacos, and enchiladas made fresh to
              order. Don’t skip the salsa fresca or guacamole—both are homemade and
              incredibly flavorful. Perfect for a quick, satisfying lunch near
              Waterfront Station.
            </p>

            {/* SECTION-7: Il Saltimbocca – Wood-Fired Italian */}
            <h2 id="section-7">Il Saltimbocca – Wood-Fired Italian Sandwiches</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Burrard & Pender (Coordinates: 49.28730, -123.11950){" "}
              <Marker
                coordY={49.28730}
                coordX={-123.11950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Weekdays ~11:30 AM – 2:00 PM <br />
              <strong>Cost:</strong> $12–$14 per sandwich
            </p>
            <p>
              Operated by the team behind Via Tevere Pizzeria, this truck offers
              Italian “saltimbocca” sandwiches fresh out of a wood-fired oven. Whether
              you choose porchetta or meatballs with rich tomato sauce, each bite is a
              taste of Naples. Be prepared for saucy, hearty goodness.
            </p>

            {/* SECTION-8: Reel Mac and Cheese */}
            <h2 id="section-8">Reel Mac and Cheese – Cheesy Comfort</h2>
            <p ref={(el) => setSpotRef("section-8", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> PNE Grounds (Coordinates: 49.28273, -123.03730){" "}
              <Marker
                coordY={49.28273}
                coordX={-123.03730}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Typically at events/festivals <br />
              <strong>Cost:</strong> $9–$14 per mac & cheese bowl
            </p>
            <p>
              Serving movie-themed mac & cheese variations, Reel Mac and Cheese
              hits the spot for serious comfort cravings. They use a blend of seven
              Canadian cheeses, ensuring each bowl is rich and gooey. Keep an eye out
              at festivals or the PNE for their rotating menu of creative “reel” names.
            </p>

            {/* SECTION-9: Shameless Buns – Filipino Fusion */}
            <h2 id="section-9">Shameless Buns – Filipino Fusion</h2>
            <p ref={(el) => setSpotRef("section-9", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Fraser St (Approx. Coordinates: 49.23400, -123.09000){" "}
              <Marker
                coordY={49.23400}
                coordX={-123.09000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Varies (often weekends and special events) <br />
              <strong>Cost:</strong> $12–$14 for sandwiches or rice bowls
            </p>
            <p>
              Vancouver’s first Filipino food truck operates out of a converted
              jeepney. The Longanisa Sandwich is a must-try, featuring sweet-savory
              sausage on soft pandesal with pickled papaya slaw. Follow their social
              media to catch them at various breweries and night markets.
            </p>

            {/* SECTION-10: Super Thai Food */}
            <h2 id="section-10">Super Thai Food – Authentic Thai Street Eats</h2>
            <p ref={(el) => setSpotRef("section-10", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Great Northern Way (Coordinates: 49.26865, -123.09830){" "}
              <Marker
                coordY={49.26865}
                coordX={-123.09830}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <strong>Hours:</strong> Weekdays 11:00 AM – 6:00 PM <br />
              <strong>Cost:</strong> $10–$13
            </p>
            <p>
              Known for its authentic pad thai and rich green curry, this truck parks
              near Emily Carr University, feeding hungry students and office workers.
              Flavors are fresh, portions are generous, and spice levels can be
              adjusted to your liking.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver requires each food truck to have a valid permit, adhere to
              zoning rules, and comply with health inspections. Trucks typically
              cannot park within 100 meters of a brick-and-mortar restaurant serving
              similar cuisine, ensuring fair competition. Check the City of
              Vancouver website for up-to-date requirements. As a customer, be aware
              that street vendors follow strict hygiene standards for your safety.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Where can I see which food trucks are open each day?</h3>
              <p>
                Use the <strong>Vancouver Street Food App</strong> or follow truck
                social media accounts for real-time location updates and schedules.
              </p>

              <h3>2. Is street food safe in Vancouver?</h3>
              <p>
                Yes. Food trucks undergo the same health inspections and licensing
                as restaurants, ensuring high standards for cleanliness and food
                safety.
              </p>

              <h3>3. What if I have dietary restrictions?</h3>
              <p>
                Many trucks offer vegetarian, vegan, or gluten-free options.
                Chickpea is entirely vegan, while other trucks can often accommodate
                dietary needs—just ask about ingredients when ordering.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s street food scene is a culinary treasure trove of flavors
              and cultural influences. Whether you’re a local on your lunch break or
              a traveler seeking the city’s best bites, these ten must-try spots
              showcase why Vancouver is on the global map for delicious street eats.
              Grab a friend, explore the vibrant neighborhoods, and treat yourself to
              some of the most memorable meals on wheels.
            </p>
            <p>
              <em>Disclaimer:</em> Information on addresses, hours, and prices is
              subject to change. Always check official resources or social media for
              the most current updates.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}